<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { PlusIcon, XIcon } from "lucide-vue-next";
import { computed, ref } from "vue";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { organizationDiscussionThreadPath } from "@/routes";

interface Props {
  tags: string[];
  availableTags?: string[];
  organizationId: number;
  threadId: number;
  canEdit?: boolean;
  showEmpty?: boolean;
  variant?: "default" | "compact";
}

const props = withDefaults(defineProps<Props>(), {
  availableTags: () => [],
  canEdit: false,
  showEmpty: true,
  variant: "default",
});

const isUpdating = ref(false);
const tagDraft = ref("");
const addPopoverOpen = ref(false);

const badgeClass = computed(() =>
  props.variant === "compact"
    ? "text-[11px] px-2.5 py-0.5"
    : "text-xs px-3 py-1",
);

const tagSuggestions = computed(() =>
  props.availableTags.filter((tag) => !props.tags.includes(tag)).slice(0, 6),
);

const normalizeTags = (value: string) =>
  value
    .split(/[\s,]+/)
    .map((tag) => tag.replace(/^#/, "").trim())
    .filter(Boolean);

const updateTags = (nextTags: string[]) => {
  if (!props.canEdit || isUpdating.value) return;
  if (!props.organizationId || !props.threadId) return;
  isUpdating.value = true;
  router.patch(
    organizationDiscussionThreadPath(props.organizationId, props.threadId),
    { discussion_thread: { tags: nextTags } },
    {
      preserveScroll: true,
      preserveState: true,
      onFinish: () => {
        isUpdating.value = false;
        tagDraft.value = "";
        addPopoverOpen.value = false;
      },
    },
  );
};

const removeTag = (tag: string) => {
  updateTags(props.tags.filter((value) => value !== tag));
};

const addTagsFromInput = () => {
  const parsed = normalizeTags(tagDraft.value);
  if (parsed.length === 0) return false;
  const nextTags = Array.from(new Set([...props.tags, ...parsed]));
  updateTags(nextTags);
  return true;
};

const addSuggestion = (tag: string) => {
  const nextTags = Array.from(new Set([...props.tags, tag]));
  updateTags(nextTags);
};

const handleTagSave = () => {
  addTagsFromInput();
};
</script>

<template>
  <div class="space-y-2">
    <div class="flex flex-wrap items-center gap-2">
      <span
        v-for="tag in props.tags"
        :key="tag"
        class="group inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-background text-muted-foreground"
        :class="badgeClass"
      >
        <span>#{{ tag }}</span>
        <button
          v-if="props.canEdit"
          type="button"
          class="-mr-1 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-muted-foreground/70 transition-colors hover:text-foreground"
          :aria-label="`Quitar ${tag}`"
          @click="removeTag(tag)"
        >
          <XIcon class="h-2.5 w-2.5" />
        </button>
      </span>
      <span
        v-if="props.tags.length === 0 && props.showEmpty"
        class="text-xs text-muted-foreground"
      >
        Sin tema
      </span>
    </div>

    <div v-if="props.canEdit" class="space-y-2">
      <Popover v-model:open="addPopoverOpen">
        <PopoverTrigger as-child>
          <Button
            type="button"
            variant="outline"
            size="sm"
            class="h-8 rounded-full"
          >
            <PlusIcon class="mr-1 h-3.5 w-3.5" />
            Agregar tema
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" class="w-64 p-3 text-sm">
          <div class="space-y-3">
            <div class="space-y-2">
              <p class="text-xs font-medium text-muted-foreground">Tema (opcional)</p>
              <Input
                v-model="tagDraft"
                placeholder="Ej: salud, compras"
                class="h-9"
                @keydown.enter.prevent="handleTagSave"
              />
              <Button
                type="button"
                size="sm"
                class="w-full"
                :disabled="isUpdating"
                @click="handleTagSave"
              >
                Guardar
              </Button>
              <p class="text-xs text-muted-foreground">
                Te ayuda a ordenar y encontrarlo después.
              </p>
            </div>

            <template v-if="tagSuggestions.length > 0">
              <Separator />
              <div class="space-y-2">
                <p class="text-xs font-medium text-muted-foreground">Sugerencias</p>
                <div class="flex flex-wrap gap-1.5">
                  <button
                    v-for="tag in tagSuggestions"
                    :key="tag"
                    type="button"
                    class="rounded-full border border-border/70 bg-muted/30 px-2 py-0.5 text-[11px] text-muted-foreground transition hover:bg-muted/60 hover:text-foreground"
                    @click="addSuggestion(tag)"
                  >
                    #{{ tag }}
                  </button>
                </div>
              </div>
            </template>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  </div>
</template>
